import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { eur, offerTotals } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";

const STAGES = ["Lead", "Contatto", "Preventivo", "Vinto", "Perso"];
const STAGE_COLORS = { Lead: "#94A3B8", Contatto: "#0EA5E9", Preventivo: "#F59E0B", Vinto: "#10B981", Perso: "#EF4444" };

export default function Pipeline() {
  const [offers, setOffers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/offers").then((r) => setOffers(r.data));
    api.get("/companies").then((r) => setCompanies(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";

  const drop = async (stage) => {
    setOverStage(null);
    if (!dragId) return;
    const offer = offers.find((o) => o.id === dragId);
    setDragId(null);
    if (!offer || offer.stage === stage) return;
    setOffers((prev) => prev.map((o) => o.id === dragId ? { ...o, stage } : o));
    try { await api.patch(`/offers/${offer.id}/stage`, { stage }); toast.success(`Spostata in "${stage}"`); }
    catch (e) { toast.error("Errore aggiornamento"); load(); }
  };

  return (
    <div className="p-10">
      <PageHeader subtitle="VENDITE" title="Pipeline" />

      <div className="flex gap-4 overflow-x-auto kanban-scroll pb-4">
        {STAGES.map((stage) => {
          const items = offers.filter((o) => o.stage === stage);
          const value = items.reduce((s, o) => s + offerTotals(o).total, 0);
          return (
            <div key={stage} data-testid={`pipeline-col-${stage}`}
              onDragOver={(e) => { e.preventDefault(); setOverStage(stage); }}
              onDragLeave={() => setOverStage(null)}
              onDrop={() => drop(stage)}
              className={`w-72 shrink-0 bg-slate-100/60 rounded-sm border transition-colors duration-150 ${overStage === stage ? "border-[#0052CC] bg-blue-50/50" : "border-slate-200"}`}>
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-sm" style={{ background: STAGE_COLORS[stage] }} />
                  <span className="font-mono-label text-[10px] text-slate-600">{stage}</span>
                  <span className="text-[10px] text-slate-400">({items.length})</span>
                </div>
                <span className="text-xs font-semibold text-slate-500">{eur(value)}</span>
              </div>
              <div className="p-3 space-y-2 min-h-[120px]">
                {items.map((o) => (
                  <div key={o.id} data-testid={`pipeline-card-${o.id}`}
                    draggable
                    onDragStart={() => setDragId(o.id)}
                    onDragEnd={() => { setDragId(null); setOverStage(null); }}
                    onClick={() => navigate(`/offerte/${o.id}`)}
                    className={`bg-white border border-slate-200 rounded-sm p-3 cursor-pointer hover:shadow-sm hover:-translate-y-px transition-transform duration-150 ${dragId === o.id ? "opacity-40" : ""}`}>
                    <div className="font-mono-label text-[8px] text-slate-400 mb-1">{o.number}</div>
                    <div className="text-sm font-medium text-slate-900 leading-snug mb-2">{o.title}</div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500 truncate max-w-[120px]">{companyName(o.company_id)}</span>
                      <span className="text-sm font-semibold text-[#0052CC]">{eur(offerTotals(o).total)}</span>
                    </div>
                  </div>
                ))}
                {items.length === 0 && <div className="text-center py-6 text-xs text-slate-300">Trascina qui</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
